import { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Save, ArrowLeft, Play, Eye, Code, Info, Copy, ChevronDown } from 'lucide-react';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface TemplateEditorProps {
    template: any;
    isNew: boolean;
    onSave: () => void;
    onCancel: () => void;
}

const TemplateEditor = ({ template, isNew, onSave, onCancel }: TemplateEditorProps) => {
    const [formData, setFormData] = useState(template);
    const [saving, setSaving] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');
    const [testEmail, setTestEmail] = useState('');
    const [showTestModal, setShowTestModal] = useState(false);
    const [showPlaceholders, setShowPlaceholders] = useState(true);
    const toast = useToast();
    const { canUpdate } = usePermissions();

    // Create local draft state
    useEffect(() => {
        setFormData(template);
    }, [template]);

    // Handle live preview generation
    useEffect(() => {
        const generatePreview = async () => {
            try {
                const res = await api.post('/email-templates/preview', {
                    type: formData.type,
                    html_content: formData.html_content,
                    // If we are editing content, we might want to pick a specific master, but for now defaults work
                });
                setPreviewHtml(res.data.html);
            } catch (err) {
                console.error('Preview failed', err);
            }
        };

        // Debounce preview
        const timer = setTimeout(generatePreview, 500);
        return () => clearTimeout(timer);
    }, [formData.html_content, formData.type]);

    const handleSave = async () => {
        try {
            setSaving(true);
            if (isNew) {
                await api.post('/email-templates', formData);
                toast.success('Created', 'Template created successfully');
            } else {
                await api.put(`/email-templates/${formData.id}`, formData);
                toast.success('Saved', 'Template updated successfully');
            }
            onSave();
        } catch (err) {
            toast.error('Error', 'Failed to save template');
        } finally {
            setSaving(false);
        }
    };

    const handleSendTest = async () => {
        if (!testEmail) return;
        try {
            await api.post('/email-templates/send-test', {
                email: testEmail,
                subject: `[TEST] ${formData.subject || 'Template Test'}`,
                html_content: previewHtml, // Send the rendered preview
                type: formData.type
            });
            toast.success('Sent', `Test email sent to ${testEmail}`);
            setShowTestModal(false);
            setTestEmail('');
        } catch (err) {
            toast.error('Error', 'Failed to send test email');
        }
    };

    return (
        <div className="h-[calc(100vh-200px)] flex flex-col">
            {/* Toolbar */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center space-x-4">
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-900">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'New Template' : 'Edit Template'}
                    </h2>
                </div>
                <div className="flex space-x-3">
                    {canUpdate && (
                        <button
                            onClick={() => setShowTestModal(true)}
                            className="btn-secondary flex items-center space-x-2"
                        >
                            <Play className="w-4 h-4" />
                            <span>Send Test</span>
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="btn-primary flex items-center space-x-2"
                        >
                            <Save className="w-4 h-4" />
                            <span>{saving ? 'Saving...' : 'Save Template'}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex gap-6 min-h-0">
                {/* Helper Panel / Form */}
                <div className="w-1/3 flex flex-col gap-4 overflow-y-auto pr-2">
                    <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="input-base w-full"
                                placeholder="e.g. Welcome Email"
                            />
                        </div>

                        <div className="flex space-x-4">
                            <div className="w-1/2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                                    className="input-base w-full"
                                >
                                    <option value="content">Content</option>
                                    <option value="master">Master</option>
                                </select>
                            </div>
                            <div className="w-1/2 flex items-center pt-6">
                                <label className="flex items-center space-x-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_default}
                                        onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">Default Template</span>
                                </label>
                            </div>
                        </div>

                        {formData.type === 'content' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                                <input
                                    type="text"
                                    value={formData.subject || ''}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    className="input-base w-full"
                                    placeholder="e.g. Welcome to SecureLink"
                                />
                            </div>
                        )}
                    </div>

                    {/* Placeholder Reference Panel */}
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <button
                            onClick={() => setShowPlaceholders(!showPlaceholders)}
                            className="w-full flex items-center justify-between text-sm font-semibold text-gray-700"
                        >
                            <span className="flex items-center space-x-2">
                                <Info className="w-4 h-4 text-primary-500" />
                                <span>Placeholder Reference</span>
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPlaceholders ? 'rotate-180' : ''}`} />
                        </button>

                        {showPlaceholders && (
                            <div className="mt-3 space-y-3">
                                {formData.type === 'master' ? (
                                    /* Master template placeholders */
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">
                                            Master templates wrap content templates. Use this reserved placeholder:
                                        </p>
                                        <div
                                            className="flex items-center justify-between p-2 bg-amber-50 rounded border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors"
                                            onClick={() => navigator.clipboard.writeText('{{content}}')}
                                            title="Click to copy"
                                        >
                                            <div>
                                                <code className="text-xs font-bold text-amber-800">{'{{content}}'}</code>
                                                <p className="text-xs text-amber-600 mt-0.5">Where the inner email body is injected (required)</p>
                                                <p className="text-[10px] text-amber-400 mt-0.5 font-mono">↳ email_templates.html_content (content template body)</p>
                                            </div>
                                            <Copy className="w-3 h-3 text-amber-400" />
                                        </div>
                                    </div>
                                ) : (
                                    /* Content template placeholders */
                                    <>
                                        <p className="text-xs text-gray-500">
                                            Click any placeholder to copy it. Available variables depend on which function uses this template.
                                        </p>

                                        {/* Invitation Placeholders */}
                                        <div>
                                            <h5 className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-1.5">
                                                Admin User Invitation
                                            </h5>
                                            <p className="text-[10px] text-gray-400 mb-2 italic">Called from: provisioningService → sendInvitationEmail()</p>
                                            <div className="space-y-1">
                                                {[
                                                    { tag: 'recipient_name', desc: 'Invitee name (email prefix)', source: 'Derived: email.split("@")[0]', example: 'john.doe' },
                                                    { tag: 'recipient_email', desc: 'Invitee email address', source: 'user_invitations.email', example: 'john.doe@company.com' },
                                                    { tag: 'tenant_name', desc: 'Tenant organization name', source: 'vsm_tenant.ten_name', example: 'Demo Corp' },
                                                    { tag: 'login_url', desc: 'Platform login/dashboard URL', source: 'Settings: general.platformUrl + "/login"', example: 'https://vsn.securelink.net/login' },
                                                    { tag: 'expiry_date', desc: 'Invitation expiry date', source: 'user_invitations.expires_at (formatted)', example: '3/5/2026' },
                                                    { tag: 'inviter_name', desc: 'Admin who sent the invite', source: 'sys_user.username (of inviting admin)', example: 'admin@company.com' },
                                                    { tag: 'platform_name', desc: 'Platform branding name', source: 'Hardcoded: "SecureLink VSN+"', example: 'SecureLink VSN+' },
                                                    { tag: 'support_email', desc: 'Support contact email', source: 'Hardcoded: "support@securelink.net"', example: 'support@securelink.net' },
                                                ].map(({ tag, desc, source, example }) => (
                                                    <div
                                                        key={tag}
                                                        className="p-1.5 rounded hover:bg-primary-50 cursor-pointer group transition-colors"
                                                        onClick={() => navigator.clipboard.writeText(`{{${tag}}}`)}
                                                        title={`Click to copy {{${tag}}}\nExample: ${example}\nSource: ${source}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <code className="text-xs font-medium text-primary-700">{`{{${tag}}}`}</code>
                                                            <Copy className="w-3 h-3 text-gray-300 group-hover:text-primary-400 flex-shrink-0" />
                                                        </div>
                                                        <p className="text-[11px] text-gray-500 mt-0.5">{desc}</p>
                                                        <p className="text-[10px] text-gray-300 group-hover:text-gray-400 font-mono mt-0.5 truncate">↳ {source}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Divider */}
                                        <hr className="border-gray-200" />

                                        {/* VPN Invitation Placeholders */}
                                        <div>
                                            <h5 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1.5">
                                                VPN User Invitation
                                            </h5>
                                            <p className="text-[10px] text-gray-400 mb-2 italic">Called from: provisioningService → sendVpnInvitationEmail()</p>
                                            <div className="space-y-1">
                                                {[
                                                    { tag: 'recipient_name', desc: 'Invitee name (email prefix)', source: 'Derived: email.split("@")[0]', example: 'john.doe' },
                                                    { tag: 'recipient_email', desc: 'Invitee email address', source: 'user_invitations.email', example: 'john.doe@company.com' },
                                                    { tag: 'tenant_name', desc: 'Tenant organization name', source: 'vsm_tenant.ten_name', example: 'Demo Corp' },
                                                    { tag: 'orchestrator_code', desc: 'Organisation code for VPN app setup', source: 'Settings: discovery.orchestratorCode', example: 'NET-LNK' },
                                                    { tag: 'tenant_code', desc: 'Alias for orchestrator_code (backward compat)', source: 'Settings: discovery.orchestratorCode', example: 'NET-LNK' },
                                                    { tag: 'dashboard_url', desc: 'Platform portal URL (for registration)', source: 'Settings: general.platformUrl', example: 'https://vsn.securelink.net' },
                                                    { tag: 'app_download_ios', desc: 'iOS App Store link', source: 'Settings: general.appDownloadIos', example: 'https://apps.apple.com/app/...' },
                                                    { tag: 'app_download_android', desc: 'Google Play Store link', source: 'Settings: general.appDownloadAndroid', example: 'https://play.google.com/store/...' },
                                                    { tag: 'app_download_macos', desc: 'macOS download link', source: 'Settings: general.appDownloadMacos', example: 'https://vsn.securelink.net/download/macos' },
                                                    { tag: 'app_download_windows', desc: 'Windows download link', source: 'Settings: general.appDownloadWindows', example: 'https://vsn.securelink.net/download/windows' },
                                                    { tag: 'expiry_date', desc: 'Invitation expiry date', source: 'user_invitations.expires_at (formatted)', example: '3/5/2026' },
                                                    { tag: 'inviter_name', desc: 'Admin who sent the invite', source: 'sys_user.username (of inviting admin)', example: 'admin@company.com' },
                                                    { tag: 'platform_name', desc: 'Platform branding name', source: 'Hardcoded: "SecureLink VSN+"', example: 'SecureLink VSN+' },
                                                    { tag: 'support_email', desc: 'Support contact email', source: 'Hardcoded: "support@securelink.net"', example: 'support@securelink.net' },
                                                ].map(({ tag, desc, source, example }) => (
                                                    <div
                                                        key={tag}
                                                        className="p-1.5 rounded hover:bg-emerald-50 cursor-pointer group transition-colors"
                                                        onClick={() => navigator.clipboard.writeText(`{{${tag}}}`)}
                                                        title={`Click to copy {{${tag}}}\nExample: ${example}\nSource: ${source}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <code className="text-xs font-medium text-emerald-700">{`{{${tag}}}`}</code>
                                                            <Copy className="w-3 h-3 text-gray-300 group-hover:text-emerald-400 flex-shrink-0" />
                                                        </div>
                                                        <p className="text-[11px] text-gray-500 mt-0.5">{desc}</p>
                                                        <p className="text-[10px] text-gray-300 group-hover:text-gray-400 font-mono mt-0.5 truncate">↳ {source}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Divider */}
                                        <hr className="border-gray-200" />

                                        {/* Alert Placeholders */}
                                        <div>
                                            <h5 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">
                                                System Alert
                                            </h5>
                                            <p className="text-[10px] text-gray-400 mb-2 italic">Called from: emailService → sendAlertEmail()</p>
                                            <div className="space-y-1">
                                                {[
                                                    { tag: 'title', desc: 'Alert title', source: 'VMAlert rule: alert name', example: 'High CPU Usage' },
                                                    { tag: 'severity', desc: 'Alert severity level', source: 'VMAlert rule: severity label (uppercased)', example: 'CRITICAL' },
                                                    { tag: 'severity_color', desc: 'Hex color for severity', source: 'Computed: critical=#DC2626, warning=#D97706, info=#2563EB', example: '#DC2626' },
                                                    { tag: 'message', desc: 'Alert description text', source: 'VMAlert rule: annotations.description', example: 'CPU usage exceeded 90%' },
                                                    { tag: 'edge_name', desc: 'Affected edge device name', source: 'vsm_device.dev_name (via edge_guid lookup)', example: 'Melbourne-Edge-01' },
                                                    { tag: 'edge_guid', desc: 'Edge device serial/GUID', source: 'VMAlert label: edge_guid', example: 'a1b2c3d4-...' },
                                                    { tag: 'metric', desc: 'Metric that triggered alert', source: 'VMAlert rule: metric expression', example: 'cpu_usage_percent' },
                                                    { tag: 'value', desc: 'Current metric value', source: 'VMAlert evaluation: current sample value', example: '95.2' },
                                                    { tag: 'timestamp', desc: 'When the alert fired', source: 'Alert.createdAt (toLocaleString)', example: '2/26/2026, 11:00 AM' },
                                                    { tag: 'dashboard_url', desc: 'Link to web dashboard', source: 'Settings: general.platformUrl', example: 'https://vsn.securelink.net' },
                                                ].map(({ tag, desc, source, example }) => (
                                                    <div
                                                        key={tag}
                                                        className="p-1.5 rounded hover:bg-amber-50 cursor-pointer group transition-colors"
                                                        onClick={() => navigator.clipboard.writeText(`{{${tag}}}`)}
                                                        title={`Click to copy {{${tag}}}\nExample: ${example}\nSource: ${source}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <code className="text-xs font-medium text-amber-700">{`{{${tag}}}`}</code>
                                                            <Copy className="w-3 h-3 text-gray-300 group-hover:text-amber-400 flex-shrink-0" />
                                                        </div>
                                                        <p className="text-[11px] text-gray-500 mt-0.5">{desc}</p>
                                                        <p className="text-[10px] text-gray-300 group-hover:text-gray-400 font-mono mt-0.5 truncate">↳ {source}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Editor & Preview Split View */}
                <div className="w-2/3 flex flex-col bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                    <div className="flex border-b border-gray-200 bg-gray-50">
                        <button
                            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 ${activeTab === 'editor' ? 'bg-white border-r border-gray-200 text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
                            onClick={() => setActiveTab('editor')}
                        >
                            <Code className="w-4 h-4" />
                            <span>HTML Source</span>
                        </button>
                        <button
                            className={`px-4 py-2 text-sm font-medium flex items-center space-x-2 ${activeTab === 'preview' ? 'bg-white border-x border-gray-200 text-primary-600' : 'text-gray-500 hover:text-gray-900'}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            <Eye className="w-4 h-4" />
                            <span>Live Preview</span>
                        </button>
                    </div>

                    <div className="flex-1 relative">
                        {/* Editor Pane */}
                        <div className={`absolute inset-0 ${activeTab === 'editor' ? 'z-10 bg-white' : 'z-0 invisible'}`}>
                            <textarea
                                value={formData.html_content}
                                onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                                className="w-full h-full p-4 font-mono text-sm resize-none focus:outline-none"
                                placeholder="<html>...</html>"
                                spellCheck={false}
                            />
                        </div>

                        {/* Preview Pane */}
                        <div className={`absolute inset-0 bg-gray-100 p-4 ${activeTab === 'preview' ? 'z-10' : 'z-0 invisible'}`}>
                            <div className="w-full h-full bg-white rounded shadow-sm overflow-hidden">
                                <iframe
                                    title="preview"
                                    srcDoc={previewHtml}
                                    className="w-full h-full border-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Test Email Modal */}
            {showTestModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h3 className="text-lg font-bold mb-4">Send Test Email</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">To Address</label>
                                <input
                                    type="email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    className="input-base w-full"
                                    placeholder="you@example.com"
                                    autoFocus
                                />
                            </div>
                            <div className="flex justify-end space-x-3 pt-2">
                                <button
                                    onClick={() => setShowTestModal(false)}
                                    className="btn-white"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSendTest}
                                    className="btn-primary"
                                    disabled={!testEmail}
                                >
                                    Send
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplateEditor;
